import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Grid, Typography, TableRow, TableCell, Chip, useTheme,
} from '@mui/material';
import { FlashOn, LocationOn, Timer } from '@mui/icons-material';
import { GlassCard, GlassTable, ShimmerSkeleton, EmptyState } from '../../../components/admin/ui';
import { getRealtime } from '../../../analytics/adminApi';
import { ADMIN_COLORS } from '../../../components/admin/ui/glassTokens';

const POLL_INTERVAL_MS = 5000;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(ts) {
  if (!ts) return '';
  const diffS = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (diffS < 5) return 'just now';
  if (diffS < 60) return `${diffS}s ago`;
  return `${Math.floor(diffS / 60)}m ago`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AnalyticsRealtime() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [, setTick] = useState(0); // force re-render for time-ago strings
  const intervalRef = useRef(null);
  const tickRef = useRef(null);

  const poll = () => {
    getRealtime().then((d) => {
      setData(d);
      setLoading(false);
    });
  };

  useEffect(() => {
    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    tickRef.current = setInterval(() => setTick((t) => t + 1), 1000);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(tickRef.current);
    };
  }, []);

  const activeCount = data?.activeCount ?? 0;
  const byPath = data?.byPath ?? [];
  const byCountry = data?.byCountry ?? [];
  const recent = data?.recent ?? [];

  return (
    <Box>
      {/* ── Active count ──────────────────────────────────────────────── */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <GlassCard
          sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: 200,
          }}
        >
          {/* Pulsing dot */}
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: ADMIN_COLORS.success,
                boxShadow: `0 0 0 0 ${ADMIN_COLORS.success}`,
                animation: 'pulse-realtime 1.8s infinite',
                '@keyframes pulse-realtime': {
                  '0%': { boxShadow: `0 0 0 0 rgba(16,185,129,0.6)` },
                  '70%': { boxShadow: `0 0 0 10px rgba(16,185,129,0)` },
                  '100%': { boxShadow: `0 0 0 0 rgba(16,185,129,0)` },
                },
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{ color: isDark ? '#F8FAFC' : '#0F172A', lineHeight: 1 }}
            >
              {loading ? '—' : activeCount.toLocaleString()}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)',
              }}
            >
              Active Users Right Now
            </Typography>
          </Box>
          <FlashOn sx={{ ml: 'auto', fontSize: 32, color: ADMIN_COLORS.success, opacity: 0.7 }} />
        </GlassCard>

        <Chip
          label={`Updates every ${POLL_INTERVAL_MS / 1000}s`}
          size="small"
          icon={<Timer sx={{ fontSize: 14 }} />}
          sx={{
            bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.06)',
            color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)',
            fontSize: '0.72rem',
          }}
        />
      </Box>

      <Grid container spacing={2}>
        {/* ── By path ────────────────────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <GlassCard sx={{ p: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 2, color: isDark ? '#F8FAFC' : '#0F172A' }}
            >
              Active by Page
            </Typography>
            {loading ? (
              <ShimmerSkeleton height={260} />
            ) : byPath.length === 0 ? (
              <EmptyState title="No active pages" iconIndex={0} />
            ) : (
              <GlassTable
                columns={[{ label: 'Page' }, { label: 'Active', align: 'right' }]}
                sx={{ maxHeight: 300 }}
              >
                {byPath.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell
                      sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.8rem' }}
                    >
                      {row.path}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={row.count}
                        size="small"
                        sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: ADMIN_COLORS.success, fontWeight: 700, fontSize: '0.72rem' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </GlassTable>
            )}
          </GlassCard>
        </Grid>

        {/* ── By country ─────────────────────────────────────────────────── */}
        <Grid item xs={12} md={3}>
          <GlassCard sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationOn sx={{ fontSize: 16, color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }} />
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ color: isDark ? '#F8FAFC' : '#0F172A' }}
              >
                Active by Country
              </Typography>
            </Box>
            {loading ? (
              <ShimmerSkeleton height={260} />
            ) : byCountry.length === 0 ? (
              <EmptyState title="No geo data" iconIndex={1} />
            ) : (
              <GlassTable
                columns={[{ label: 'Country' }, { label: 'Active', align: 'right' }]}
                sx={{ maxHeight: 300 }}
              >
                {byCountry.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.country || '(unknown)'}</TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
              </GlassTable>
            )}
          </GlassCard>
        </Grid>

        {/* ── Recent activity feed ────────────────────────────────────────── */}
        <Grid item xs={12} md={5}>
          <GlassCard sx={{ p: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 2, color: isDark ? '#F8FAFC' : '#0F172A' }}
            >
              Recent Activity
            </Typography>
            {loading ? (
              <ShimmerSkeleton height={260} />
            ) : recent.length === 0 ? (
              <EmptyState title="No recent activity" iconIndex={2} />
            ) : (
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {recent.map((item, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      py: 1,
                      borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.05)'}`,
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.78rem',
                          color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(15,23,42,0.8)',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.path}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)', fontSize: '0.7rem' }}
                      >
                        {item.city || item.country || 'Unknown location'}
                        {item.userId && ` · uid:${String(item.userId).slice(0, 8)}`}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        flexShrink: 0,
                        ml: 1,
                        color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,23,42,0.35)',
                        fontSize: '0.7rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {timeAgo(item.ts)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </GlassCard>
        </Grid>
      </Grid>
    </Box>
  );
}
